<template>
    <div
        ref="dropZone"
        :class="[{ 'forms__field_input--file--dropping': isOverDropZone }, `forms__field--${props.size}`]"
    >
        <header class="forms__field_input--file__header flex items-start justify-between gap-2">
            <div>
                <Button color="default" dusk="file-upload" type="button" :size="size" @click="focusFileInput">
                    <icon icon="hero/outline/arrow-up-tray" class="w-3.5 h-3.5 mr-1"></icon>
                    {{ lang(multiple ? 'fields::fields.file.choose_multiple' : 'fields::fields.file.choose') }}
                </Button>
                <span class="forms__field_input--file__header__info">
                    <template v-if="multiple">
                        {{ lang('fields::fields.file.drop_here_to_add') }}
                    </template>
                    <template v-else-if="isEmpty">
                        {{ lang('fields::fields.file.drop_here_to_upload') }}
                    </template>
                    <template v-else>
                        {{ lang('fields::fields.file.drop_here_to_replace') }}
                    </template>
                </span>
            </div>

            <FileSortDropdown
                v-if="sortControls && display.length > 1"
                class="shrink-0"
                :labels="sortControls"
                :model-value="sortState"
                @update:model-value="applySort"
            />
        </header>

        <div v-if="hasPreviews" class="forms__field_input--file__previews">
            <!-- Display all files -->
            <file-preview-list
                :orderable="orderable && isManualView"
                :is-featured="isFeatured"
                :files="display"
                :size="props.size"
                :interactive="props.interactive"
                :actions="props.actions"
                :params="props.params"
                :upload-progress="uploadProgress"
                @open="openMediaInfo"
                @remove="remove($event)"
                @update:set-featured="setFeatured"
                @update:model-value="sorted"
            />
        </div>

        <input
            :id="id"
            ref="input"
            type="file"
            :dusk="dusk"
            v-bind="$attrs"
            :multiple="multiple"
            :accept="accept"
            hidden
            @input="updateValue"
        />

        <footer v-if="selectFromMediaLibrary" class="forms__field_input--file__footer">
            <div class="forms__field_input--file__footer__actions">
                <Button color="default" dusk="file-gallery" type="button" :size="size" @click="openMediaGallery">
                    <icon icon="hero/outline/photo" class="w-3.5 h-3.5 mr-1"></icon>
                    {{
                        lang(
                            multiple
                                ? 'fields::fields.file.choose_gallery_multiple'
                                : 'fields::fields.file.choose_gallery_multiple',
                        )
                    }}
                </Button>

                <Button
                    v-if="field && field.chooseFromGallery"
                    color="default"
                    dusk="file-gallery"
                    type="button"
                    :size="size"
                    @click="openMediaGalleries"
                >
                    {{
                        lang(
                            multiple
                                ? 'fields::fields.file.choose_from_galleries'
                                : 'fields::fields.file.choose_from_galleries',
                        )
                    }}
                </Button>
            </div>

            <div v-if="showRecommended" class="forms__field_input--file__recommended">
                <span
                    v-if="getRecommended.sizes && getRecommended.sizes.length > 0"
                    v-tooltip="{
                        message: `${lang('fields::fields.file.other_recommended_sizes')}: ${getRecommended.sizes.join(
                            ', ',
                        )}`,
                        position: 'top-right',
                    }"
                >
                    {{ lang('fields::fields.file.recommended') }}
                    {{ getRecommended.width }} x {{ getRecommended.height }}
                    <span v-if="getRecommended.aspect">({{ getRecommended.aspect }})</span>
                </span>
                <span v-else>
                    {{ lang('fields::fields.file.recommended') }}
                    {{ getRecommended.width }} x {{ getRecommended.height }}
                    <span v-if="getRecommended.aspect">({{ getRecommended.aspect }})</span>
                </span>
            </div>
        </footer>

        <GalleryModal
            v-if="showMediaGallery"
            :selected="modelValue"
            :multiple="multiple"
            @close="closeMediaGallery"
            @select="selectMedia"
        />

        <GalleryStructuredModal
            v-if="showMediaGalleries"
            :selected="modelValue"
            :multiple="multiple"
            @close="closeMediaGalleries"
            @select="selectMedia"
        ></GalleryStructuredModal>

        <MediaModal v-if="showMediaInfo" :item="mediaInfo" @close="closeMediaInfo" @updated="updated" />
    </div>
</template>

<script setup lang="ts">
import { watch, ref, computed, reactive } from 'vue';
import type { Ref } from 'vue';
import { useDropZone } from '@vueuse/core';
import { dateTimeHumanReadableFromISO, dateTimeHumanReadableFromUnix } from '@src/Common/Helpers/dates';
import type { SavedFile, PreviewFile } from '@src/Fields/Pages/Files/types';
import FilePreviewList from '@src/Fields/Pages/Files/FilePreviewList.vue';
import FileSortDropdown from '@src/Fields/Pages/Files/FileSortDropdown.vue';
import { compareFilesBy } from '@src/Fields/Pages/Files/fileSorting';
import type { FileSort, FileSortLabels } from '@src/Fields/Pages/Files/fileSorting';
import GalleryModal from '@src/Media/Pages/GalleryModal.vue';
import GalleryStructuredModal from '@src/Media/Pages/GalleryStructuredModal.vue';
import MediaModal from '@src/Media/Pages/MediaModal.vue';
import type { FieldComponent } from '@src/Common/Components/types';
import { useToast } from '@src/Common/Composables/useToast';

type FileValue = File | FileList | SavedFile | (File | SavedFile)[] | null;

interface FileSizeRecommendation {
    width: number | null;
    height: number | null;
    aspect: string | null;
    sizes: string[];
}

const emit = defineEmits<{
    (event: 'update:modelValue', content: FileValue): void;
    (event: 'update:setFeatured', content: number): void;
    (event: 'processing:start'): void;
    (event: 'processing:end'): void;
}>();

const props = withDefaults(
    defineProps<FieldComponent & { useS3ForMedia?: boolean; uploadDisk?: string | null; uploadDirectory?: string }>(),
    {
    modelValue: null,
    dusk: null,
    id: null,
    file: null,
    icon_preview: 'hero/outline/document',
    icon_color: 'gray',
    multiple: false,
    size: 'base',
    orderable: false,
    isFeatured: false,
    alt: null,
    accept: '',
    interactive: true,
    useS3ForMedia: false,
    uploadDisk: null,
    uploadDirectory: '',
    recommended: () => ({
        aspect: null,
        width: null,
        height: null,
        sizes: [],
    }),
    actions: () => [],
    params: () => ({
        selectFromMediaLibrary: true,
    }),
});

const input = ref();
const dropZone = ref(null);
const accept = ref(props.accept);
const selectFromMediaLibrary = computed(() => props.params?.selectFromMediaLibrary ?? true);
const replaceMediaId = computed<number | null>(() => {
    const raw = props.params?.replaceMediaId;
    return typeof raw === 'number' ? raw : null;
});

const modelValue: Ref<FileValue> = ref(props.modelValue ?? []);

// Upload progress (0–100) per in-flight File, keyed by the File instance so the
// preview built from it can look its progress up reactively.
const uploadProgress = reactive(new Map<File, number>());

/**
 * Get the preview object for the file
 *
 * @param file
 * @param isSavedFile
 */
const getPreviewFileObject = (file, isSavedFile = false) => {
    const preview = {
        is_image: isSavedFile ? file.is_image : file.type && file.type.startsWith('image'),
        name: file.name,
        url: isSavedFile ? file.url : URL.createObjectURL(file),
        icon: isSavedFile ? file.icon : props.icon_preview,
        icon_color: isSavedFile ? file.icon_color : props.icon_color,
        size: file.size,
        mime: file.mime,
        created_at: isSavedFile
            ? dateTimeHumanReadableFromISO(file.created_at)
            : dateTimeHumanReadableFromUnix(file.lastModified),
        is_featured: isSavedFile ? (file.pivot ? file.pivot.is_featured : false) : false,
        alt: isSavedFile ? file.alt : props.alt,
        valid: isSavedFile ? file.valid : true,
        position: isSavedFile ? (file.position ?? file.pivot?.position ?? null) : null,
    };

    if (isSavedFile) {
        preview.id = file.id;
    } else {
        preview.sourceFile = file;
    }

    return preview;
};

/**
 * Map the file value to array of preview objects
 *
 * @param value
 */
const mapToPreview = (value: FileValue): PreviewFile[] => {
    if (!value) {
        return [];
    }

    if (value instanceof File) {
        return [getPreviewFileObject(value)];
    }

    if (value instanceof FileList) {
        return Array.from(value).map((file) => getPreviewFileObject(file));
    }

    if (typeof value === 'string') {
        return [
            { name: value, url: value, size: 0, mime: '', created_at: '', is_image: false, icon: '', icon_color: '' },
        ];
    }

    if (Array.isArray(value)) {
        return value.filter((item) => item != null).map((item) => getPreviewFileObject(item, 'is_image' in item));
    }

    if ('is_image' in value) {
        return [getPreviewFileObject(value, true)];
    }

    return [];
};

/**
 * Optional sort controls above the preview list, enabled via
 * params.display.sort_controls = { position?, name?, created_at? }.
 * `manual_order` makes the persisted position the default view. A name/created_at
 * header sort only reorders a copy for display and never mutates modelValue, so a
 * transient view sort is never persisted on the next save (only drag reorders it).
 */
const manualOrder = computed<boolean>(() => props.params?.display?.manual_order ?? false);

const sortControls = computed((): FileSortLabels | null => props.params?.display?.sort_controls ?? null);

const sortState = ref<FileSort | null>(manualOrder.value ? { key: 'position', dir: 1 } : null);

const isManualView = computed(() => !sortState.value || sortState.value.key === 'position');

// created_at on a preview is already human-formatted, so a view sort must read the
// underlying model item, not the preview.
const rawSortable = (item: any) => ({
    name: item?.name,
    created_at: typeof item?.created_at === 'string' ? item.created_at : '',
    position: item?.position ?? item?.pivot?.position ?? null,
});

const buildDisplay = (): PreviewFile[] => {
    const sort = sortState.value;

    // Manual/position view keeps the live model array order — for saved data the backend
    // already position-sorted it, and a drag reorders the array in place.
    if (!Array.isArray(modelValue.value) || !sort || sort.key === 'position') {
        return mapToPreview(modelValue.value);
    }

    const comparator = compareFilesBy(sort);
    const ordered = [...modelValue.value].sort((a, b) => comparator(rawSortable(a), rawSortable(b)));

    return mapToPreview(ordered);
};

const display: Ref<PreviewFile[]> = ref(buildDisplay());

const rebuildDisplay = () => {
    display.value = buildDisplay();
};

const hasPreviews = computed(() => {
    return display.value.length > 0;
});

const remove = (file: PreviewFile) => {
    display.value = display.value.filter((entry: PreviewFile) =>
        entry && entry.id ? entry.id !== file.id : entry.url !== file.url,
    );

    // Remove from modelValue
    if (props.multiple && Array.isArray(modelValue.value)) {
        modelValue.value = modelValue.value.filter((entry: File | SavedFile) => entry.name !== file.name);
    } else {
        modelValue.value = null;
    }

    const inputFiles = Array.from(input.value.files);
    const filteredFiles = inputFiles.filter((single) => single.name !== file.name);
    const newFiles = new DataTransfer();
    filteredFiles.forEach((f) => newFiles.items.add(f));
    input.value.files = newFiles.files;
};

const focusFileInput = () => {
    input.value.click();
};

const updateValue = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const files = target.files as FileList;

    if (!props.multiple) {
        // Only one file allowed, replace the existing one
        modelValue.value = files[0];
        rebuildDisplay();
    } else {
        // Multiple files allowed, append to the existing files
        const newFilesArray = Array.from(files);
        const currentModelValue = Array.isArray(modelValue.value) ? modelValue.value : [];
        modelValue.value = [...currentModelValue, ...newFilesArray];
        rebuildDisplay();
    }
};

function onFileDrop(files: File[] | null) {
    if (!files) return;

    const acceptedFiles = files.filter((file) => {
        const acceptList = accept.value.split(',').map((item) => item.trim());

        // If acceptList is empty or contains 1 empty '' item, accept all files
        if (acceptList.length === 0 || (acceptList.length === 1 && acceptList[0] === '')) {
            return true;
        }

        return acceptList.some((type) => {
            if (type === 'image/*') {
                return file.type.startsWith('image/');
            } else if (type.startsWith('.')) {
                return file.name.endsWith(type);
            } else {
                return file.type === type;
            }
        });
    });

    if (!props.multiple) {
        // Only one file allowed, replace the existing one
        modelValue.value = acceptedFiles[0] || null;
        rebuildDisplay();
    } else {
        // Multiple files allowed, append to the existing files
        const currentModelValue = Array.isArray(modelValue.value) ? modelValue.value : [];
        modelValue.value = [...currentModelValue, ...acceptedFiles];
        rebuildDisplay();
    }
}

const { isOverDropZone } = useDropZone(dropZone, {
    onDrop: onFileDrop,
});

const sortKey = (item: { id?: number | string; name?: string } | null): unknown => item?.id ?? item?.name;

const sorted = (data: PreviewFile[]) => {
    if (!props.orderable || !Array.isArray(modelValue.value)) {
        return;
    }

    // Reorder modelValue to mirror the dragged preview order. Match by id (falling back to
    // name) and CONSUME each match, so duplicate filenames map one-to-one instead of every
    // duplicate collapsing onto the first index.
    const pool = [...modelValue.value] as ({ id?: number | string; name?: string } | null)[];

    const reordered = data
        .map((preview) => {
            const index = pool.findIndex((item) => sortKey(item) === sortKey(preview));
            return index === -1 ? null : pool.splice(index, 1)[0];
        })
        .filter((item): item is { id?: number | string; name?: string } => item != null);

    reordered.push(...(pool.filter((item) => item != null) as { id?: number | string; name?: string }[]));

    // Mutate in place (same array reference). Reassigning would round-trip through the props
    // watcher and re-key the Sortable list mid-drag, duplicating rows — SortableJS owns the DOM.
    (modelValue.value as unknown[]).splice(0, modelValue.value.length, ...reordered);
    emit('update:modelValue', modelValue.value);
};

const applySort = (sort: FileSort) => {
    sortState.value = sort;
    rebuildDisplay();
};

const setFeatured = (index: number) => {
    if (props.isFeatured) {
        display.value.forEach((file, i) => {
            file.is_featured = i === index;
        });

        if (Array.isArray(modelValue.value)) {
            modelValue.value.forEach((file, i) => {
                file.is_featured = i === index;
            });
        }

        emit('update:modelValue', modelValue.value);
        emit('update:setFeatured', index);
    }
};

const isEmpty = computed(() => display.value.length === 0);

/**
 * Gallery
 */
const showMediaGallery = ref(false);

const openMediaGallery = () => {
    showMediaGallery.value = true;
};

const closeMediaGallery = () => {
    showMediaGallery.value = false;
};

const selectMedia = (item: any) => {
    if (props.multiple) {
        modelValue.value = item;
    } else {
        modelValue.value = item;
    }

    rebuildDisplay();

    showMediaGallery.value = false;
};

/**
 * Galleries
 */
const showMediaGalleries = ref(false);

const openMediaGalleries = () => {
    showMediaGalleries.value = true;
};

const closeMediaGalleries = () => {
    showMediaGalleries.value = false;
};

/**
 * Recommendation
 */
const getRecommended = computed((): FileSizeRecommendation | null => {
    if (
        props.recommended &&
        (props.recommended.width ||
            props.recommended.height ||
            props.recommended.aspect ||
            (props.recommended.sizes && props.recommended.sizes > 0))
    ) {
        return {
            width: props.recommended.width,
            height: props.recommended.height,
            aspect: props.recommended.aspect,
            sizes: props.recommended.sizes,
        };
    }

    if (
        props.field &&
        props.field.recommended &&
        (props.field.recommended.width ||
            props.field.recommended.height ||
            props.field.recommended.aspect ||
            (props.field.recommended.sizes && props.field.recommended.sizes > 0))
    ) {
        return {
            width: props.field.recommended.width,
            height: props.field.recommended.height,
            aspect: props.field.recommended.aspect,
            sizes: props.field.recommended.sizes,
        };
    }

    return null;
});

const showRecommended = computed(() => {
    return !!getRecommended.value;
});

/**
 * Individual media
 */
const mediaInfo = ref(null);
const showMediaInfo = ref(false);

const openMediaInfo = (file: PreviewFile) => {
    mediaInfo.value = file;
    showMediaInfo.value = true;
};

const closeMediaInfo = () => {
    showMediaInfo.value = false;
};

const updated = (value) => {
    if (!value || !value.id) return;

    if (props.multiple && Array.isArray(modelValue.value)) {
        const index = modelValue.value.findIndex((file) => parseInt(file.id, 10) === parseInt(value.id, 10));
        if (index !== -1) {
            modelValue.value.splice(index, 1, value);
        }
    } else {
        modelValue.value = value;
    }

    rebuildDisplay();
};

/**
 * Upload newly added files on background and change them to Saved files
 */
const processingCount = ref(0);

const startProcessing = () => {
    if (processingCount.value === 0) {
        emit('processing:start');
    }
    processingCount.value++;
};

const endProcessing = () => {
    processingCount.value--;
    if (processingCount.value <= 0) {
        processingCount.value = 0;
        emit('processing:end');
    }
};

// Minimal set of Media fields kept in form.content for an uploaded attachment.
// A full Media toArray() carries `exif` and the `children` thumbnail relation
// (each child another full media with its own exif) — ~400 nested formData
// fields per attachment, which blows past PHP's max_input_vars and silently
// truncates the POST (only ~3 attachments survive at 1000, ~13 at 5000).
// Covers display (getPreviewFileObject), KUPR sending (re-fetches Media::find
// by id) and migrations. The canonical Media row in the DB keeps everything.
const SLIM_MEDIA_FIELDS = [
    'id',
    'name',
    'path',
    'url',
    'mime',
    'size',
    'storage_disk',
    'is_image',
    'icon',
    'icon_color',
    'alt',
    'valid',
    'width',
    'height',
    'created_at',
    'pivot',
];

/**
 * Reduce an uploaded Media object to the minimal fields needed by the form,
 * dropping heavy/nested data (exif, children, …) before it is stored.
 */
const slimMedia = (media) => {
    if (!media || typeof media !== 'object') {
        return media;
    }

    const slim = {};
    for (const key of SLIM_MEDIA_FIELDS) {
        if (key in media) {
            slim[key] = media[key];
        }
    }

    return slim;
};

const uploadNewlyAddedFiles = () => {
    if (Array.isArray(modelValue.value)) {
        const files = modelValue.value;

        files.forEach((file, index) => {
            // Only upload if it's a File instance (not already uploaded Media object)
            if (file instanceof File) {
                // Upload the file
                const formData = new FormData();
                formData.append('file', file);

                // Add S3 configuration if enabled
                if (props.useS3ForMedia) {
                    formData.append('use_s3', '1');
                }

                // Pin the upload to this field's target disk/directory so a matching thumbnail
                // profile applies from the first byte (server allowlists the disk).
                if (props.uploadDisk) {
                    formData.append('disk', props.uploadDisk);
                    formData.append('directory', props.uploadDirectory ?? '');
                }

                startProcessing();
                uploadProgress.set(file, 0);

                axios
                    .post(route('media.admin.api.create'), formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                        onUploadProgress: (event) => {
                            if (event.total) {
                                uploadProgress.set(file, Math.round((event.loaded / event.total) * 100));
                            }
                        },
                    })
                    .then(({ data }) => {
                        // Replace the File instance with the Media object
                        modelValue.value[index] = slimMedia(data.object);
                        rebuildDisplay();

                        if (data.message) {
                            useToast({ message: data.message });
                        }
                    })
                    .catch((error) => {
                        useToast({ message: error.response?.data?.message ?? error.message, type: 'error' });

                        // Drop the failed File so it does not linger in the form
                        // and ride along raw with the item create/update request
                        const failedIndex = files.indexOf(file);

                        if (failedIndex !== -1) {
                            files.splice(failedIndex, 1);
                        }

                        rebuildDisplay();
                    })
                    .finally(() => {
                        uploadProgress.delete(file);
                        endProcessing();
                    });
            }
        });
    } else if (modelValue.value instanceof File) {
        // Handle single file upload
        const singleFile = modelValue.value;
        const formData = new FormData();
        formData.append('file', singleFile);

        // Add S3 configuration if enabled
        if (props.useS3ForMedia) {
            formData.append('use_s3', '1');
        }

        // Pin the upload to this field's target disk/directory (server allowlists the disk).
        // Only for new uploads — a replace keeps the existing Media's path.
        if (props.uploadDisk && !replaceMediaId.value) {
            formData.append('disk', props.uploadDisk);
            formData.append('directory', props.uploadDirectory ?? '');
        }

        // When replaceMediaId is set we route the upload through the update
        // endpoint so the existing Media record is replaced in place instead
        // of creating a sibling with a `_N` suffix.
        const targetId = !props.multiple ? replaceMediaId.value : null;
        const targetUrl = targetId
            ? route('media.admin.api.update', { id: targetId })
            : route('media.admin.api.create');

        if (targetId) {
            formData.append('_method', 'PUT');
        }

        startProcessing();
        uploadProgress.set(singleFile, 0);

        axios
            .post(targetUrl, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (event) => {
                    if (event.total) {
                        uploadProgress.set(singleFile, Math.round((event.loaded / event.total) * 100));
                    }
                },
            })
            .then(({ data }) => {
                // Replace the File instance with the Media object
                modelValue.value = slimMedia(data.object);
                rebuildDisplay();

                if (data.message) {
                    useToast({ message: data.message });
                }
            })
            .catch((error) => {
                useToast({ message: error.response?.data?.message ?? error.message, type: 'error' });

                // Drop the failed File so it does not ride along raw with the
                // item request. Replace flow keeps the File — nulling it would
                // silently detach the original media on save.
                if (!targetId) {
                    modelValue.value = null;
                    rebuildDisplay();
                }
            })
            .finally(() => {
                uploadProgress.delete(singleFile);
                endProcessing();
            });
    }
};

watch(modelValue, (value: FileValue) => {
    emit('update:modelValue', value);
});

watch(
    () => props.modelValue,
    (newValue: FileValue) => {
        modelValue.value = newValue;
        rebuildDisplay();

        // Only upload files when they are actually File instances (newly added)
        // Don't upload when props change with already processed Media objects
        // Also check if the new value actually contains File instances before uploading
        const hasNewFiles = Array.isArray(newValue)
            ? newValue.some((file) => file instanceof File)
            : newValue instanceof File;

        if (hasNewFiles) {
            uploadNewlyAddedFiles();
        }
    },
);
</script>
